import { create } from "zustand";
import {
  CaptableExtractionStats,
  CaptableRunData,
  CaptableRunStatus,
  EnqueueCaptablePayload,
  cancelCaptableRun,
  enqueueCaptable,
} from "../api/dataPipeline";

export interface CaptableEntry {
  runId: string;
  cin: string;
  jobId: string;
  status: CaptableRunStatus;
  phase: string | null;
  resultUrl: string | null;
  urlExpiry: string | null;
  // Generated Stage-4 onboarding workbook — null until the run completes
  // (and stays null when the stage-4 fill failed).
  stage4Url: string | null;
  error: string | null;
  extractionStats: CaptableExtractionStats | null;
  requestedAt: number;
  // Set true optimistically after the user clicks Cancel, until the run
  // reaches a terminal status. Drives the "Cancelling…" UI state.
  cancelling?: boolean;
}

export type CaptableRequestResult =
  | { kind: "started"; entry: CaptableEntry }
  | { kind: "running"; entry: CaptableEntry }
  | { kind: "error"; error: string };

export type CaptableCancelResult =
  | { kind: "cancelled" }
  | { kind: "alreadyFinished"; status: CaptableRunStatus }
  | { kind: "error"; error: string };

const TERMINAL_STATUSES: CaptableRunStatus[] = ["COMPLETED", "FAILED"];

export function isCaptableRunInFlight(
  entry: CaptableEntry | undefined,
): boolean {
  return !!entry && !TERMINAL_STATUSES.includes(entry.status);
}

// Minimal data needed to start tracking a run enqueued outside this store
// (e.g. one item of a batch enqueue response).
export interface TrackRunInit {
  runId: string;
  cin: string;
  jobId: string;
  status: string;
}

interface CaptableStore {
  runs: Record<string, CaptableEntry>;
  enqueueRun: (
    payload: EnqueueCaptablePayload,
  ) => Promise<CaptableRequestResult>;
  // Register an already-enqueued run so the manager starts polling it.
  trackRun: (init: TrackRunInit) => void;
  applyStatus: (data: CaptableRunData) => void;
  cancelRun: (runId: string) => Promise<CaptableCancelResult>;
  clearRun: (runId: string) => void;
  getRunsForCin: (cin: string) => CaptableEntry[];
}

export const useCaptableStore = create<CaptableStore>((set, get) => ({
  runs: {},

  enqueueRun: async (payload) => {
    try {
      const res = await enqueueCaptable(payload);
      const existing = get().runs[res.runId];
      const entry: CaptableEntry = {
        runId: res.runId,
        cin: payload.cin,
        jobId: res.jobId,
        status: (res.status as CaptableRunStatus) ?? "QUEUED",
        phase: null,
        resultUrl: null,
        urlExpiry: null,
        stage4Url: null,
        error: null,
        extractionStats: null,
        requestedAt: existing?.requestedAt ?? Date.now(),
      };
      set((s) => ({ runs: { ...s.runs, [entry.runId]: entry } }));
      if (res.deduped) return { kind: "running", entry };
      return { kind: "started", entry };
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to enqueue captable.";
      return { kind: "error", error: msg };
    }
  },

  trackRun: (init) => {
    set((s) => {
      const existing = s.runs[init.runId];
      const entry: CaptableEntry = {
        runId: init.runId,
        cin: init.cin,
        jobId: init.jobId,
        status: (init.status as CaptableRunStatus) ?? "QUEUED",
        phase: existing?.phase ?? null,
        resultUrl: existing?.resultUrl ?? null,
        urlExpiry: existing?.urlExpiry ?? null,
        stage4Url: existing?.stage4Url ?? null,
        error: existing?.error ?? null,
        extractionStats: existing?.extractionStats ?? null,
        requestedAt: existing?.requestedAt ?? Date.now(),
      };
      return { runs: { ...s.runs, [entry.runId]: entry } };
    });
  },

  applyStatus: (data) => {
    set((s) => {
      const existing = s.runs[data.runId];
      if (!existing) return s;
      const next: CaptableEntry = {
        ...existing,
        status: data.status,
        phase: data.phase,
        resultUrl: data.resultUrl,
        urlExpiry: data.urlExpiry,
        stage4Url: data.stage4Url ?? existing.stage4Url,
        error: data.error,
        extractionStats: data.extractionStats ?? existing.extractionStats,
        // Once the run reaches a terminal status the optimistic flag is moot.
        cancelling: TERMINAL_STATUSES.includes(data.status)
          ? false
          : existing.cancelling,
      };
      return { runs: { ...s.runs, [data.runId]: next } };
    });
  },

  cancelRun: async (runId) => {
    const existing = get().runs[runId];
    // Idempotent on the server, but skip the optimistic churn if it's gone or done.
    if (existing && TERMINAL_STATUSES.includes(existing.status)) {
      return { kind: "alreadyFinished", status: existing.status };
    }
    // Optimistically flag the run as cancelling.
    set((s) => {
      const cur = s.runs[runId];
      if (!cur) return s;
      return { runs: { ...s.runs, [runId]: { ...cur, cancelling: true } } };
    });
    try {
      const res = await cancelCaptableRun(runId);
      // The run had already finished; reflect the authoritative status and
      // clear the optimistic flag. Polling will fill in the rest of the fields.
      set((s) => {
        const cur = s.runs[runId];
        if (!cur) return s;
        return {
          runs: {
            ...s.runs,
            [runId]: { ...cur, status: res.status, cancelling: false },
          },
        };
      });
      if (res.cancelled) return { kind: "cancelled" };
      return { kind: "alreadyFinished", status: res.status };
    } catch (err: any) {
      // Roll back the optimistic flag so the user can retry.
      set((s) => {
        const cur = s.runs[runId];
        if (!cur) return s;
        return { runs: { ...s.runs, [runId]: { ...cur, cancelling: false } } };
      });
      const msg =
        err?.response?.data?.errorMessage ??
        err?.response?.data?.message ??
        err?.message ??
        "Failed to cancel run.";
      return { kind: "error", error: msg };
    }
  },

  clearRun: (runId) => {
    set((s) => {
      const { [runId]: _removed, ...rest } = s.runs;
      return { runs: rest };
    });
  },

  getRunsForCin: (cin) =>
    Object.values(get().runs).filter((r) => r.cin === cin),
}));
